// ─── meteoraLp: DLMM position reads for WalletHoldings ───
//
// DLMM stays READ-ONLY (claim/withdraw for DLMM is done on meteora.ag).
// DAMM v2 positions are read over RPC and can be opened / claimed / removed
// from here — see the ACTION LAYER below. DLMM reads come from the documented data API
// (dlmm.datapi.meteora.ag — the host the tank gauge uses server-side), never
// the @meteora-ag/dlmm SDK: that SDK is ~86 RPC calls per load and is the
// exact architecture that makes Meteora's own site feel heavy. It stays out
// of this bundle entirely; when the action stage lands (add/claim/withdraw),
// it loads via dynamic import behind the button press, not before.
//
// Endpoint (from meteora-invent/skills/meteora/references/data-and-apis.md):
//   GET /portfolio/open?user=<wallet>   — open positions (+ PnL), param is `user`
//
// TWO HONESTY NOTES, both lessons paid for this week:
//
// 1. CORS is UNVERIFIED. The tank fetch runs server-side partly for this
//    reason. If the browser is blocked, this module fails GRACEFULLY into
//    status:'blocked' and the panel says so in one quiet line — and the
//    fallback is a one-line route on the server's existing :8766 CORS proxy.
//    It does not retry-hammer a blocked origin.
//
// 2. The response SCHEMA is now mapped from the PUBLISHED OpenAPI spec
//    (docs.meteora.ag → developer-guides/dlmm/api-reference/openapi.json,
//    schema PoolOpenPortfolioItem, read 2026-08-18) — camelCase throughout,
//    numeric values as STRINGS, and one entry per POOL: value/fees/PnL are
//    aggregates of that pool's positions, with per-position detail limited
//    to the address lists (listPositions / positionsOutOfRange — exactly
//    what claim/withdraw will need). The original snake_case probes stay as
//    fallbacks, and when nothing usable is found the module still logs the
//    actual keys ONCE (`[MeteoraLP] unparsed …`) instead of rendering an
//    empty section forever — a parser that fails silently is how the gauge
//    lost an evening. Spec-verified is not fixture-verified: the first live
//    position (window.__lp() dumps state any moment) is still the proof.

const LP_API = 'https://dlmm.datapi.meteora.ag/portfolio/open'
const TTL_MS = 30_000
const cache = new Map()          // wallet -> { ts, status, positions, raw }
const inflight = new Map()
const subs = new Set()           // () => void, fired when a fetch settles
let _loggedShape = false

// Known quote mints — used only to ORIENT a pair so `mintX` is always the
// user-facing token. DLMM convention puts the token on the X side of most
// TOKEN-SOL / TOKEN-USDC pools; this normalizes the exceptions, because the
// panel keys LP lines on mintX (lpByMint) and a SOL-TOKEN oriented pool
// would otherwise pin its line to the SOL row. Originals survive in _raw.
const QUOTE_MINTS = new Set([
  'So11111111111111111111111111111111111111112', // wSOL
  'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', // USDC
  'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', // USDT
])

const num = (...vals) => {
  for (const v of vals) {
    if (v == null) continue
    const n = typeof v === 'string' ? parseFloat(v) : Number(v)
    if (Number.isFinite(n)) return n
  }
  return null
}

/** Normalize one portfolio entry into what the panel renders.
 *  Field map is from the published spec (PoolOpenPortfolioItem): one entry
 *  per POOL, camelCase, numerics as strings — num() parses them. The old
 *  snake_case probes trail each documented field as fallbacks. */
function normPosition(p) {
  if (!p || typeof p !== 'object') return null
  const pool = p.poolAddress || p.pool || p.pair || p.lb_pair || p.pool_address || p.pair_address || null
  // Symbols are PLAIN STRINGS in the spec (tokenX / tokenY); the nested
  // token_x.symbol shape stays as a variant fallback.
  let symX = (typeof p.tokenX === 'string' ? p.tokenX : null) || p.token_x?.symbol || p.token_x_symbol || null
  let symY = (typeof p.tokenY === 'string' ? p.tokenY : null) || p.token_y?.symbol || p.token_y_symbol || null
  let mintX = p.tokenXMint || p.token_x?.address || p.token_x_mint || p.mint_x || null
  let mintY = p.tokenYMint || p.token_y?.address || p.token_y_mint || p.mint_y || null
  if (mintX && mintY && QUOTE_MINTS.has(mintX) && !QUOTE_MINTS.has(mintY)) {
    ;[mintX, mintY] = [mintY, mintX]
    ;[symX, symY] = [symY, symX]
  }
  const name = (symX && symY ? `${symX}-${symY}` : null) || p.name || p.pool_name || p.pair_name || null
  const valueUsd = num(p.balances, p.total_value_usd, p.value_usd, p.total_usd, p.position_value_usd, p.usd_value)
  const feesUsd = num(p.unclaimedFees, p.total_fee_usd, p.fees_usd, p.fee_usd, p.unclaimed_fee_usd, p.total_fees_usd)
  const pnlUsd = num(p.pnl, p.pnl_usd, p.total_pnl_usd)
  const pnlPct = num(p.pnlPctChange)
  // Range PRICES are not in this response — the price triple stays only as a
  // fallback for schema variants. The real signal is the pool-level
  // `outOfRange` flag: nullable and INVERTED (true = at least one of the
  // user's positions in this pool sits outside its range).
  const lower = num(p.lower_bin_price, p.min_price, p.price_lower, p.lower_price)
  const upper = num(p.upper_bin_price, p.max_price, p.price_upper, p.upper_price)
  const active = num(p.poolPrice, p.active_bin_price, p.current_price, p.active_price)
  let inRange = null
  if (p.outOfRange === true) inRange = false
  else if (p.outOfRange === false) inRange = true
  else if (lower != null && upper != null && active != null) inRange = active >= lower && active <= upper
  else if (typeof p.in_range === 'boolean') inRange = p.in_range
  // Carried for the ACTION stage: claim/withdraw operate on position
  // addresses, add needs pool + binStep — the API hands all of it to us
  // here, so the SDK only touches chain state at transaction-build time.
  const positions = Array.isArray(p.listPositions) ? p.listPositions : []
  const positionsOutOfRange = Array.isArray(p.positionsOutOfRange) ? p.positionsOutOfRange : []
  return {
    pool, name, mintX, mintY, valueUsd, feesUsd, pnlUsd, pnlPct,
    lower, upper, active, inRange,
    positions, positionsOutOfRange,
    positionCount: num(p.openPositionCount) ?? (positions.length || null),
    binStep: num(p.binStep),
    depositUsd: num(p.totalDeposit),
    _raw: p,
  }
}

const PAGE_SIZE = 50
const MAX_PAGES = 10

async function fetchWallet(wallet) {
  try {
    // Pages are 1-based ({ page, pageSize, hasNext }); follow hasNext so a
    // wallet with more pools than one page doesn't silently lose the rest.
    let raw = null
    let pools = []
    for (let page = 1; page <= MAX_PAGES; page++) {
      const res = await fetch(`${LP_API}?user=${wallet}&page=${page}&page_size=${PAGE_SIZE}`)
      if (!res.ok) {
        if (page === 1) return { ts: Date.now(), status: `http_${res.status}`, positions: [], raw: null }
        break   // keep what we have rather than dropping page 1 too
      }
      const body = await res.json()
      if (page === 1) raw = body
      if (!Array.isArray(body?.pools)) break
      pools = pools.concat(body.pools)
      if (!body.hasNext || !body.pools.length) break
    }
    if (raw && Array.isArray(raw.pools)) raw = { ...raw, pools }
    // Real shape (verified live): { page, pageSize, hasNext, totalCount,
    // totalPositions, pools: [...] } — the array is `pools`, and an empty
    // portfolio returns totalPositions: 0 with pools: []. The other spellings
    // stay as fallbacks in case the endpoint varies by account state.
    const arr = Array.isArray(raw?.pools) ? raw.pools
      : Array.isArray(raw) ? raw
      : Array.isArray(raw?.data) ? raw.data
      : Array.isArray(raw?.positions) ? raw.positions
      : null
    if (!arr) {
      if (!_loggedShape) {
        _loggedShape = true
        console.warn('[MeteoraLP] unparsed portfolio shape — keys:',
          raw && typeof raw === 'object' ? Object.keys(raw).slice(0, 14) : typeof raw)
      }
      return { ts: Date.now(), status: 'unparsed', positions: [], raw }
    }
    // A pool entry may itself carry several positions; flatten either shape.
    const flat = []
    for (const entry of arr) {
      const inner = entry?.positions
      if (Array.isArray(inner) && inner.length) {
        for (const pos of inner) flat.push({ ...entry, ...pos })
      } else {
        flat.push(entry)
      }
    }
    const positions = flat.map(normPosition).filter(Boolean).map(p => ({ ...p, kind: 'dlmm' }))
    if (positions.length && positions.every(p => p.valueUsd == null) && !_loggedShape) {
      _loggedShape = true
      console.warn('[MeteoraLP] positions parsed but no value field matched — first raw:', arr[0])
    }
    return { ts: Date.now(), status: 'ok', positions, raw }
  } catch (e) {
    // TypeError('Failed to fetch') with no status = CORS or network — treat
    // as blocked and stop asking until TTL expires. Never hammer.
    return { ts: Date.now(), status: 'blocked', positions: [], raw: null, err: String(e) }
  }
}

// ─── Read model: EVENT-DRIVEN, not polled ───
// The first cut polled — a 1Hz component tick over a 30s TTL, so ~120
// requests an hour against an API we don't control, running whether or not
// the panel was even on screen, for data that changes when the USER does
// something. Positions move on open/add/claim/withdraw and on price leaving
// a range; the first four are events we see, and the fifth is what the
// manual refresh and the open-panel fetch are for.
//
// `getLpPositions` is now a pure cache READ — safe to call during render,
// never fires a request. Requests happen only via ensureLpPositions (panel
// opened) or refreshLpPositions (explicit).

/** Pure read. Returns cached state or null; never triggers a fetch. */
export function getLpPositions(wallet) {
  if (!wallet) return null
  return cache.get(wallet) || null
}

/** Subscribe to cache changes — components re-render when data lands,
 *  instead of on a timer. */
export function onLpChange(fn) {
  subs.add(fn)
  return () => subs.delete(fn)
}

/** Fetch if missing or stale. Call on panel open / wallet change / after a
 *  trade. Deduped and TTL-guarded, so repeated calls are nearly free. */
export function ensureLpPositions(wallet, { force = false } = {}) {
  if (!wallet) return null
  const hit = cache.get(wallet)
  if (!force && hit && Date.now() - hit.ts < TTL_MS) return hit
  if (inflight.has(wallet)) return hit || null
  // DLMM (REST) and DAMM v2 (RPC) are fetched together; either can fail
  // without hiding the other. `status` stays the DLMM status (ok / blocked /
  // http_N / unparsed), `v2Status` is 'ok' | 'error'.
  const p = Promise.all([fetchWallet(wallet), fetchV2Wallet(wallet)]).then(([d, v2]) => {
    const v = {
      ...d,
      positions: [...d.positions, ...v2.positions],
      v2Status: v2.status,
      v2Err: v2.err || null,
    }
    cache.set(wallet, v)
    inflight.delete(wallet)
    for (const fn of subs) { try { fn() } catch {} }
    return v
  })
  inflight.set(wallet, p)
  return hit || null
}

/** Explicit refresh — the ↻ button, or after a position-changing action. */
export function refreshLpPositions(wallet) {
  return ensureLpPositions(wallet, { force: true })
}

if (typeof window !== 'undefined') {
  window.__lp = (w) => (w ? cache.get(w) : [...cache.entries()].map(([k, v]) => ({ wallet: k.slice(0, 6), ...v })))
}
// ════════════════════════════════════════════════════════════════════
//  DAMM v2 ACTION LAYER — open a position from Cheddar
// ════════════════════════════════════════════════════════════════════
//
// WHY THIS EXISTS SEPARATELY FROM THE DLMM READS ABOVE.
// Everything above talks to dlmm.datapi.meteora.ag. That path is proven —
// browser reaches it, no CORS block, clean 200 — but it only knows DLMM.
// DAMM v2 is a different program with a different indexer, and it has NO
// portfolio REST endpoint at all (its data API publishes pools / OHLCV /
// stats and nothing wallet-scoped). So v2 goes through
// @meteora-ag/cp-amm-sdk over RPC. The DLMM reads stay untouched.
//
// EVERY SDK SYMBOL BELOW WAS READ OFF damm-v2-sdk/docs.md, NOT GUESSED:
//   cpAmm.fetchPoolStatesByTokenMint(mint)
//     -> [{ publicKey, account: PoolState }]   (2 getProgramAccounts scans)
//   cpAmm.fetchPoolState(pool) -> PoolState
//   cpAmm.getDepositQuote({ inAmount, isTokenA, minSqrtPrice, maxSqrtPrice,
//     sqrtPrice, collectFeeMode, tokenAAmount, tokenBAmount, liquidity })
//     -> { actualInputAmount, consumedInputAmount, liquidityDelta, outputAmount }
//   cpAmm.createPositionAndAddLiquidity({ owner, pool, positionNft,
//     liquidityDelta, maxAmountTokenA, maxAmountTokenB, tokenAAmountThreshold,
//     tokenBAmountThreshold, tokenAMint, tokenBMint, tokenAProgram,
//     tokenBProgram }) -> TxBuilder
// Anything the docs did not state (decimals, which token program owns each
// mint) is READ FROM CHAIN below rather than assumed.
//
// TWO INTEGRATION FACTS THAT SHAPE THIS FILE:
//
// 1. TxBuilder.transaction() yields a LEGACY Transaction. Cheddar is
//    VersionedTransaction end to end — jupiter.js, Privy's adapter
//    (serialize -> remote sign -> VersionedTransaction.deserialize) and the
//    local signer's `transaction.sign([kp])` are all the Versioned API, and
//    legacy `sign()` takes varargs not an array. So the instructions are
//    lifted off the built transaction and recompiled to a v0 message. All
//    three wallet modes then travel the exact path they already use.
//
// 2. Opening a position needs TWO signatures: the owner, and a fresh mint
//    keypair for the position NFT. That keypair is EPHEMERAL — generated
//    here, used once, never stored, never shown. The user signs once, same
//    as a swap. Order is load-bearing: the WALLET SIGNS FIRST, then the NFT
//    signature is added locally. VersionedTransaction.sign() writes only the
//    slots for the signers it is given, so the wallet's signature survives.
//    Pre-signing instead would make the NFT signature ride through Privy's
//    serialize/remote-sign/deserialize round trip, and whether a foreign
//    signature survives that is undocumented. This ordering depends on
//    nothing but our own code.

const V2_RPC = import.meta.env.VITE_RPC_URL || 'https://rpc.cheddarterminal.app/rpc'
const WSOL = 'So11111111111111111111111111111111111111112'
const V2_REST = 'https://damm-v2.datapi.meteora.ag/pools'

let _v2mod = null
/** Load SDK + web3 once, lazily. Nothing here is in the entry bundle: the
 *  chart path never touches this module's action half. */
async function v2deps() {
  if (_v2mod) return _v2mod
  const [sdk, web3, spl] = await Promise.all([
    import('@meteora-ag/cp-amm-sdk'),
    import('@solana/web3.js'),
    // Already installed as a dependency of cp-amm-sdk. Used only to unpack
    // Token-2022 mints so transfer fees can be priced into the deposit quote.
    import('@solana/spl-token'),
  ])
  const CpAmm = sdk.CpAmm || sdk.default?.CpAmm
  if (!CpAmm) {
    console.warn('[MeteoraV2] package exports:', Object.keys(sdk))
    throw new Error('CpAmm export missing from @meteora-ag/cp-amm-sdk')
  }
  // BN comes along with the SDK's own dependency tree; fall back to bn.js.
  let BN = sdk.BN || sdk.default?.BN
  if (!BN) BN = (await import('bn.js')).default
  const connection = new web3.Connection(V2_RPC, 'confirmed')
  _v2mod = { sdk, web3, spl, CpAmm, BN, connection, cpAmm: new CpAmm(connection) }
  return _v2mod
}

/** Mint decimals AND owning token program, read from chain in ONE call.
 *  The token program matters: classic SPL and Token-2022 mints need
 *  different program ids in the deposit params, and guessing wrong makes the
 *  instruction fail at send time rather than here. */
async function mintInfo(mints) {
  const { web3, spl, connection } = await v2deps()
  const keys = mints.map(m => new web3.PublicKey(m))
  const infos = await connection.getMultipleAccountsInfo(keys)
  return infos.map((info, i) => {
    if (!info) throw new Error(`Mint account not found: ${mints[i]}`)
    // SPL Mint layout: decimals is a u8 at offset 44 for both SPL and
    // Token-2022 (the base mint layout is identical; 2022 appends
    // extensions after it).
    const out = { mint: mints[i], decimals: info.data[44], program: info.owner }
    // Token-2022 transfer fee: the program adds it on top of what the
    // liquidity needs and checks the TOTAL against the threshold, so the
    // quote must know about it or the add fails with 6002 ExceededSlippage.
    if (info.owner.equals(spl.TOKEN_2022_PROGRAM_ID)) {
      const m = spl.unpackMint(keys[i], info, info.owner)
      if (spl.getTransferFeeConfig(m)) out.feeMint = m
    }
    return out
  })
}

const toBase = (BN, uiAmount, decimals) => {
  const s = String(uiAmount).trim()
  if (!s || !Number.isFinite(Number(s)) || Number(s) <= 0) throw new Error('Amount must be a positive number')
  const [whole, frac = ''] = s.split('.')
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals)
  return new BN((whole || '0') + padded)
}

/** Every DAMM v2 pool holding this mint, richest first.
 *  Costs two getProgramAccounts scans, so this runs on a +LP CLICK, never
 *  on render. */
export async function findV2Pools(mint) {
  const { cpAmm, web3 } = await v2deps()
  const found = await cpAmm.fetchPoolStatesByTokenMint(new web3.PublicKey(String(mint).trim()))
  const rows = found.map(({ publicKey, account }) => ({
    pool: publicKey.toBase58(),
    tokenAMint: account.tokenAMint.toBase58(),
    tokenBMint: account.tokenBMint.toBase58(),
    isTokenA: account.tokenAMint.toBase58() === String(mint).trim(),
    liquidity: account.liquidity?.toString?.() ?? null,
    _state: account,
  }))
  // Deepest liquidity first — BN strings compare correctly by length then
  // lexically, which avoids forcing huge Q64 values through Number().
  rows.sort((a, b) => {
    const x = a.liquidity || '0', y = b.liquidity || '0'
    return x.length !== y.length ? y.length - x.length : (x < y ? 1 : x > y ? -1 : 0)
  })
  console.log(`[MeteoraV2] ${rows.length} pool(s) for ${String(mint).slice(0, 6)}`, rows)
  return rows
}

/** What a deposit of `uiAmount` of `mint` would take on the other side.
 *  Pure read — builds nothing, signs nothing, sends nothing. */
export async function quoteV2Deposit({ pool, mint, uiAmount }) {
  const { cpAmm, web3, BN, connection } = await v2deps()
  const ps = await cpAmm.fetchPoolState(new web3.PublicKey(String(pool).trim()))
  const a = ps.tokenAMint.toBase58(), b = ps.tokenBMint.toBase58()
  const target = String(mint).trim()
  if (target !== a && target !== b) throw new Error('Mint is not in this pool')
  const isTokenA = target === a
  const [infoA, infoB] = await mintInfo([a, b])
  const inAmount = toBase(BN, uiAmount, isTokenA ? infoA.decimals : infoB.decimals)
  // Transfer-fee mints (Token-2022): pass mint + epoch so the SDK takes the
  // fee off the input before sizing liquidity and adds it onto the pair side.
  // Without this both thresholds come out short and the add fails with 6002.
  let currentEpoch = 0
  if (infoA.feeMint || infoB.feeMint) currentEpoch = (await connection.getEpochInfo('confirmed')).epoch
  const feeInfo = (info) => (info.feeMint ? { mint: info.feeMint, currentEpoch } : undefined)
  const quote = await cpAmm.getDepositQuote({
    inAmount,
    isTokenA,
    inputTokenInfo: feeInfo(isTokenA ? infoA : infoB),
    outputTokenInfo: feeInfo(isTokenA ? infoB : infoA),
    minSqrtPrice: ps.sqrtMinPrice,
    maxSqrtPrice: ps.sqrtMaxPrice,
    sqrtPrice: ps.sqrtPrice,
    collectFeeMode: ps.collectFeeMode,
    tokenAAmount: ps.tokenAAmount,
    tokenBAmount: ps.tokenBAmount,
    liquidity: ps.liquidity,
  })
  const pairDecimals = isTokenA ? infoB.decimals : infoA.decimals
  const out = {
    pool: String(pool).trim(), isTokenA,
    inAmount, quote, poolState: ps, infoA, infoB,
    // UI-friendly: how much of the OTHER token this deposit also needs.
    pairMint: isTokenA ? b : a,
    pairUiAmount: Number(quote.outputAmount.toString()) / 10 ** pairDecimals,
    pairIsSol: (isTokenA ? b : a) === WSOL,
    ts: Date.now(),
  }
  return out
}

// ─── Sending: compute budget, simulate, sign, send, confirm (with err check) ───
//
// Every DAMM v2 action goes through sendV2Tx so open / claim / remove share
// one hardened path:
//   1. SDK instructions are recompiled to v0 (see fact 1 above) with a
//      compute-unit limit and a MODEST priority fee prepended.
//   2. Simulated BEFORE the wallet is asked to sign (sigVerify off), so a
//      deposit that would fail never costs a signature or a fee. The
//      simulation's unitsConsumed sizes the real CU limit.
//   3. Signed (wallet first, then any ephemeral signer), sent with
//      skipPreflight, then CONFIRMED BY POLLING getSignatureStatuses and
//      checking `err` — a landed-but-failed transaction is a failure, not
//      "Position opened". The raw tx is rebroadcast every few seconds until
//      it lands or its blockhash expires.
//   4. Outcome is one of: 'confirmed' (ok), throws (failed on chain, carries
//      .signature), 'expired' (blockhash passed without it landing — it can
//      no longer land, safe to retry), or 'pending' (RPC couldn't tell us in
//      time — check Solscan before retrying).

const CU_PRICE_MICROLAMPORTS = 50_000   // ~0.00002 SOL at 400k CU — modest
const CU_MAX = 1_400_000
const CONFIRM_MAX_MS = 90_000
const SOL_RESERVE_LAMPORTS = 20_000_000  // 0.02 SOL kept back for rent + fees

const sleep = (ms) => new Promise(r => setTimeout(r, ms))

class TxFailedError extends Error {
  constructor(signature, err) {
    super(`Transaction failed on-chain: ${JSON.stringify(err)}`)
    this.signature = signature
    this.txErr = err
  }
}

function simErrorText(value) {
  const logs = value.logs || []
  const hint = logs.find(l => /insufficient/i.test(l))
    || [...logs].reverse().find(l => /Error|failed/i.test(l))
  if (hint && /insufficient/i.test(hint)) return 'Simulation failed: insufficient balance for this transaction.'
  return `Simulation failed: ${JSON.stringify(value.err)}${hint ? ` — ${hint.replace(/^Program log: /, '').slice(0, 140)}` : ''}`
}

async function waitForSignature({ connection, signature, raw, lastValidBlockHeight }) {
  const t0 = Date.now()
  let lastSend = t0
  while (Date.now() - t0 < CONFIRM_MAX_MS) {
    await sleep(1500)
    try {
      const { value: [st] } = await connection.getSignatureStatuses([signature])
      if (st?.err) throw new TxFailedError(signature, st.err)
      if (st && (st.confirmationStatus === 'confirmed' || st.confirmationStatus === 'finalized')) return 'confirmed'
      if (!st && Date.now() - lastSend > 3000) {
        lastSend = Date.now()
        const height = await connection.getBlockHeight('confirmed')
        if (height > lastValidBlockHeight) {
          // Blockhash is dead. One last look through history before saying so.
          const { value: [st2] } = await connection.getSignatureStatuses([signature], { searchTransactionHistory: true })
          if (st2?.err) throw new TxFailedError(signature, st2.err)
          if (st2) return st2.confirmationStatus === 'processed' ? 'pending' : 'confirmed'
          return 'expired'
        }
        connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 0 }).catch(() => {})
      }
    } catch (e) {
      if (e instanceof TxFailedError) throw e
      // RPC hiccup — keep polling until the wall clock runs out.
    }
  }
  return 'pending'
}

/** One-shot status lookup for a signature the UI is holding as 'pending'.
 *  Returns 'confirmed' | 'failed' | 'pending' | 'unknown'. Read-only. */
export async function checkV2Signature(signature) {
  const { connection } = await v2deps()
  const { value: [st] } = await connection.getSignatureStatuses([signature], { searchTransactionHistory: true })
  if (!st) return 'unknown'
  if (st.err) return 'failed'
  return st.confirmationStatus === 'processed' ? 'pending' : 'confirmed'
}

async function sendV2Tx({ instructions, owner, signTransaction, extraSigners = [], onStatus = () => {} }) {
  if (!owner) throw new Error('No wallet connected')
  if (typeof signTransaction !== 'function') throw new Error('signTransaction is required')
  const { web3, connection } = await v2deps()
  const { ComputeBudgetProgram, PublicKey, TransactionMessage, VersionedTransaction } = web3
  const ownerPk = new PublicKey(String(owner))
  const cbId = ComputeBudgetProgram.programId.toBase58()
  const core = instructions.filter(ix => ix.programId.toBase58() !== cbId)
  const build = (units, blockhash) => new VersionedTransaction(
    new TransactionMessage({
      payerKey: ownerPk,
      recentBlockhash: blockhash,
      instructions: [
        ComputeBudgetProgram.setComputeUnitLimit({ units }),
        ComputeBudgetProgram.setComputeUnitPrice({ microLamports: CU_PRICE_MICROLAMPORTS }),
        ...core,
      ],
    }).compileToV0Message()
  )

  onStatus('simulating')
  const pre = await connection.getLatestBlockhash('confirmed')
  const sim = await connection.simulateTransaction(build(CU_MAX, pre.blockhash), {
    sigVerify: false, replaceRecentBlockhash: true, commitment: 'confirmed',
  })
  if (sim.value.err) {
    console.error('[MeteoraV2] simulation failed', sim.value.err, sim.value.logs)
    throw new Error(simErrorText(sim.value))
  }
  const used = sim.value.unitsConsumed || 0
  const units = used > 0 ? Math.min(CU_MAX, Math.ceil(used * 1.2) + 10_000) : 400_000

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
  onStatus('signing')
  const signed = await signTransaction(build(units, blockhash))   // wallet fills its own slot
  if (extraSigners.length) signed.sign(extraSigners)               // ephemeral signers fill theirs

  onStatus('sending')
  const raw = signed.serialize()
  const signature = await connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 0 })
  onStatus('confirming', { signature })
  const state = await waitForSignature({ connection, signature, raw, lastValidBlockHeight })
  onStatus(state === 'confirmed' ? 'done' : state, { signature })
  return { signature, state }
}

// ─── Balances (read-only) — for max and the pre-send pair check ───

/** Raw on-chain balances for `mints` (BigInt base units) plus native SOL.
 *  wSOL is reported as NATIVE lamports: the SDK wraps from native SOL. */
export async function getV2Balances(owner, mints) {
  const { web3, connection } = await v2deps()
  const ownerPk = new web3.PublicKey(String(owner))
  const lamports = BigInt(await connection.getBalance(ownerPk, 'confirmed'))
  const out = { lamports }
  await Promise.all([...new Set(mints)].filter(m => m && m !== WSOL).map(async (m) => {
    const r = await connection.getParsedTokenAccountsByOwner(ownerPk, { mint: new web3.PublicKey(m) }, 'confirmed')
    let raw = BigInt(0)
    let decimals = null
    for (const a of r.value) {
      const ta = a.account.data?.parsed?.info?.tokenAmount
      if (ta) { raw += BigInt(ta.amount); decimals = ta.decimals }
    }
    out[m] = { raw, decimals }
  }))
  if (mints.includes(WSOL)) {
    const spendable = lamports > BigInt(SOL_RESERVE_LAMPORTS) ? lamports - BigInt(SOL_RESERVE_LAMPORTS) : BigInt(0)
    out[WSOL] = { raw: spendable, decimals: 9, native: true }
  }
  return out
}

export const SOL_RESERVE = SOL_RESERVE_LAMPORTS / 1e9

/** Exact decimal string from base units — no floats, no exponent notation. */
export function rawToUiString(raw, decimals) {
  const s = BigInt(raw).toString()
  if (!decimals) return s
  const p = s.padStart(decimals + 1, '0')
  const whole = p.slice(0, -decimals)
  const frac = p.slice(-decimals).replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : whole
}

/** What the open would charge, at most, per side — the displayed quote IS
 *  the bound. Slippage (~1%) is taken out of the LIQUIDITY rather than added
 *  on top of the amounts: liquidityDelta is shaved by slippageBps and the
 *  per-side thresholds stay at the quoted amounts. So both sides get the
 *  buffer, and nothing can pull more than the numbers on screen (which is
 *  what the balance check compares against). */
export function v2DepositBounds(q, slippageBps = 100) {
  const tokenRaw = BigInt(q.inAmount.toString())
  const pairRaw = BigInt(q.quote.outputAmount.toString())
  return {
    tokenRaw, pairRaw,
    maxARaw: q.isTokenA ? tokenRaw : pairRaw,
    maxBRaw: q.isTokenA ? pairRaw : tokenRaw,
    slippageBps,
  }
}

/** Open a DAMM v2 position: create the position NFT and add liquidity in ONE
 *  transaction, from the quote the user is LOOKING AT (no silent requote —
 *  the panel requotes visibly when the quote is stale).
 *
 *  signTransaction comes straight from useDualWallet(), so local / Privy /
 *  browser all work without a mode branch here. */
export async function openV2Position({
  quote: q, owner, signTransaction,
  slippageBps = 100, onStatus = () => {},
}) {
  if (!q?.quote) throw new Error('No quote — enter an amount first')
  if (!owner) throw new Error('No wallet connected')
  const { cpAmm, web3, BN } = await v2deps()
  const { poolState: ps, isTokenA, inAmount, quote, infoA, infoB } = q

  const maxAmountTokenA = isTokenA ? inAmount : quote.outputAmount
  const maxAmountTokenB = isTokenA ? quote.outputAmount : inAmount
  const liquidityDelta = quote.liquidityDelta.mul(new BN(10_000 - slippageBps)).div(new BN(10_000))

  // EPHEMERAL. Generated here, signs once, never stored or shown.
  const positionNft = web3.Keypair.generate()
  const ownerPk = new web3.PublicKey(String(owner))

  onStatus('building')
  // TxBuilder in cp-amm-sdk 1.4.5 is Promise<Transaction> — the awaited
  // value IS the legacy Transaction (there is no .transaction() on it).
  const legacy = await cpAmm.createPositionAndAddLiquidity({
    owner: ownerPk,
    pool: new web3.PublicKey(String(q.pool).trim()),
    positionNft: positionNft.publicKey,
    liquidityDelta,
    maxAmountTokenA,
    maxAmountTokenB,
    tokenAAmountThreshold: maxAmountTokenA,
    tokenBAmountThreshold: maxAmountTokenB,
    tokenAMint: ps.tokenAMint,
    tokenBMint: ps.tokenBMint,
    tokenAProgram: infoA.program,
    tokenBProgram: infoB.program,
  })

  const r = await sendV2Tx({
    instructions: legacy.instructions, owner, signTransaction,
    extraSigners: [positionNft], onStatus,
  })
  return { ...r, positionNft: positionNft.publicKey.toBase58(), pool: String(q.pool).trim() }
}

// ─── DAMM v2 position reads (RPC) ───
// DAMM v2 has no wallet-scoped REST endpoint, so positions come from
// cpAmm.getPositionsByUser (one Token-2022 account scan + one multi-fetch),
// then one fetchPoolState per distinct pool and one mint read. USD prices
// are best-effort from the pools REST endpoint; without them the readout
// falls back to token amounts.

const ui = (bn, decimals) => Number(bn.toString()) / 10 ** decimals

async function v2PoolMeta(pool) {
  try {
    const res = await fetch(`${V2_REST}/${pool}`)
    if (!res.ok) return null
    return await res.json()
  } catch { return null }
}

async function fetchV2Wallet(wallet) {
  try {
    const { cpAmm, web3, sdk } = await v2deps()
    const rows = await cpAmm.getPositionsByUser(new web3.PublicKey(wallet))
    if (!rows.length) return { status: 'ok', positions: [] }
    const poolKeys = [...new Set(rows.map(r => r.positionState.pool.toBase58()))]
    const states = await Promise.all(poolKeys.map(k => cpAmm.fetchPoolState(new web3.PublicKey(k))))
    const metas = await Promise.all(poolKeys.map(v2PoolMeta))
    const byPool = new Map(poolKeys.map((k, i) => [k, { ps: states[i], meta: metas[i] }]))
    const mints = [...new Set(states.flatMap(ps => [ps.tokenAMint.toBase58(), ps.tokenBMint.toBase58()]))]
    const infos = await mintInfo(mints)
    const dec = new Map(infos.map(i => [i.mint, i.decimals]))

    const positions = rows.map(({ position, positionNftAccount, positionState: st }) => {
      const pool = st.pool.toBase58()
      const { ps, meta } = byPool.get(pool)
      const a = ps.tokenAMint.toBase58(), b = ps.tokenBMint.toBase58()
      const decA = dec.get(a), decB = dec.get(b)
      const total = st.unlockedLiquidity.add(st.vestedLiquidity).add(st.permanentLockedLiquidity)
      let amtA = 0, amtB = 0
      if (!total.isZero()) {
        try {
          const wq = cpAmm.getWithdrawQuote({
            liquidityDelta: total,
            minSqrtPrice: ps.sqrtMinPrice, maxSqrtPrice: ps.sqrtMaxPrice, sqrtPrice: ps.sqrtPrice,
            collectFeeMode: ps.collectFeeMode,
            tokenAAmount: ps.tokenAAmount, tokenBAmount: ps.tokenBAmount, liquidity: ps.liquidity,
          })
          amtA = ui(wq.outAmountA, decA); amtB = ui(wq.outAmountB, decB)
        } catch { amtA = null; amtB = null }
      }
      let feeA = null, feeB = null
      try {
        const f = sdk.getUnClaimLpFee(ps, st)
        feeA = ui(f.feeTokenA, decA); feeB = ui(f.feeTokenB, decB)
      } catch {}
      // A DAMM v2 pool has ONE price range shared by every position in it.
      // Full-range pools can't go out of range, so the badge is omitted.
      const fullRange = ps.sqrtMinPrice.lte(sdk.MIN_SQRT_PRICE) && ps.sqrtMaxPrice.gte(sdk.MAX_SQRT_PRICE)
      const inRange = fullRange ? null : (ps.sqrtPrice.gt(ps.sqrtMinPrice) && ps.sqrtPrice.lt(ps.sqrtMaxPrice))
      const pA = Number(meta?.token_x?.address === a ? meta.token_x.price : NaN)
      const pB = Number(meta?.token_y?.address === b ? meta.token_y.price : NaN)
      const priced = Number.isFinite(pA) && Number.isFinite(pB)
      let symA = meta?.token_x?.symbol || a.slice(0, 4)
      let symB = meta?.token_y?.symbol || b.slice(0, 4)
      // Orient like normPosition: mintX is the user-facing (non-quote) token.
      const flip = QUOTE_MINTS.has(a) && !QUOTE_MINTS.has(b)
      const X = flip
        ? { mint: b, sym: symB, amt: amtB, fee: feeB }
        : { mint: a, sym: symA, amt: amtA, fee: feeA }
      const Y = flip
        ? { mint: a, sym: symA, amt: amtA, fee: feeA }
        : { mint: b, sym: symB, amt: amtB, fee: feeB }
      return {
        kind: 'v2',
        pool,
        position: position.toBase58(),
        positionNftAccount: positionNftAccount.toBase58(),
        name: meta?.name || `${X.sym}-${Y.sym}`,
        mintX: X.mint, mintY: Y.mint, symX: X.sym, symY: Y.sym,
        amountX: X.amt, amountY: Y.amt, feeX: X.fee, feeY: Y.fee,
        valueUsd: priced && amtA != null ? amtA * pA + amtB * pB : null,
        feesUsd: priced && feeA != null ? feeA * pA + feeB * pB : null,
        pnlUsd: null,
        inRange,
        // Permanent lock can never be withdrawn (fees still claimable).
        permanentLocked: !st.permanentLockedLiquidity.isZero(),
        vesting: !st.vestedLiquidity.isZero(),
      }
    })
    return { status: 'ok', positions }
  } catch (e) {
    console.warn('[MeteoraV2] position read failed', e)
    return { status: 'error', positions: [], err: String(e?.message || e) }
  }
}

/** Positions this wallet holds (DAMM v2), normalized for the panel. */
export async function getV2Positions(walletAddress) {
  const r = await fetchV2Wallet(String(walletAddress).trim())
  if (r.status !== 'ok') throw new Error(r.err || 'DAMM v2 read failed')
  return r.positions
}

// ─── DAMM v2 actions on an existing position ───
// Both re-read position + pool state from chain at build time (the cached
// readout can be up to 30s old) and go through sendV2Tx.

async function freshV2(lp) {
  const { cpAmm, web3, sdk } = await v2deps()
  const position = new web3.PublicKey(lp.position)
  const st = await cpAmm.fetchPositionState(position)
  const ps = await cpAmm.fetchPoolState(st.pool)
  return { position, st, ps, positionNftAccount: sdk.derivePositionNftAccount(st.nftMint) }
}

/** Claim the position's unclaimed trading fees to the wallet. */
export async function claimV2Fees({ lp, owner, signTransaction, onStatus = () => {} }) {
  const { cpAmm, web3, sdk } = await v2deps()
  onStatus('building')
  const { position, st, ps, positionNftAccount } = await freshV2(lp)
  const tx = await cpAmm.claimPositionFee({
    owner: new web3.PublicKey(String(owner)),
    position,
    pool: st.pool,
    positionNftAccount,
    tokenAMint: ps.tokenAMint,
    tokenBMint: ps.tokenBMint,
    tokenAVault: ps.tokenAVault,
    tokenBVault: ps.tokenBVault,
    tokenAProgram: sdk.getTokenProgram(ps.tokenAFlag),
    tokenBProgram: sdk.getTokenProgram(ps.tokenBFlag),
  })
  return sendV2Tx({ instructions: tx.instructions, owner, signTransaction, onStatus })
}

/** Full withdraw: claims fees, removes ALL liquidity and closes the position
 *  (position + NFT rent comes back) in one transaction. Minimum outs are the
 *  current withdraw quote (net of any Token-2022 transfer fee) less
 *  slippageBps. Claim needs no change: claim_position_fee has no min-outs. */
export async function removeV2Position({ lp, owner, signTransaction, slippageBps = 100, onStatus = () => {} }) {
  const { cpAmm, web3, sdk, BN, connection } = await v2deps()
  onStatus('building')
  const { position, st, ps, positionNftAccount } = await freshV2(lp)
  if (!st.permanentLockedLiquidity.isZero()) throw new Error('Position is permanently locked — liquidity cannot be withdrawn.')
  const vest = await cpAmm.getAllVestingsByPosition(position)
  const vestings = vest.map(v => ({ account: v.publicKey, vestingState: v.account }))
  const currentPoint = await sdk.getCurrentPoint(connection, ps.activationType)
  let tokenAAmountThreshold = new BN(0)
  let tokenBAmountThreshold = new BN(0)
  if (!st.unlockedLiquidity.isZero()) {
    // Token-2022 transfer fee: the program checks the min-outs against what
    // ARRIVES (after the fee), so the quote must take the fee off first or a
    // fee above the slippage cut fails with 6002 ExceededSlippage.
    const [infoA, infoB] = await mintInfo([ps.tokenAMint.toBase58(), ps.tokenBMint.toBase58()])
    let currentEpoch = 0
    if (infoA.feeMint || infoB.feeMint) currentEpoch = (await connection.getEpochInfo('confirmed')).epoch
    const feeInfo = (info) => (info.feeMint ? { mint: info.feeMint, currentEpoch } : undefined)
    const wq = cpAmm.getWithdrawQuote({
      liquidityDelta: st.unlockedLiquidity,
      minSqrtPrice: ps.sqrtMinPrice, maxSqrtPrice: ps.sqrtMaxPrice, sqrtPrice: ps.sqrtPrice,
      tokenATokenInfo: feeInfo(infoA),
      tokenBTokenInfo: feeInfo(infoB),
      collectFeeMode: ps.collectFeeMode,
      tokenAAmount: ps.tokenAAmount, tokenBAmount: ps.tokenBAmount, liquidity: ps.liquidity,
    })
    const cut = (v) => v.mul(new BN(10_000 - slippageBps)).div(new BN(10_000))
    tokenAAmountThreshold = cut(wq.outAmountA)
    tokenBAmountThreshold = cut(wq.outAmountB)
  }
  const tx = await cpAmm.removeAllLiquidityAndClosePosition({
    owner: new web3.PublicKey(String(owner)),
    position,
    positionNftAccount,
    poolState: ps,
    positionState: st,
    tokenAAmountThreshold,
    tokenBAmountThreshold,
    vestings,
    currentPoint,
  })
  return sendV2Tx({ instructions: tx.instructions, owner, signTransaction, onStatus })
}

/** Pool discovery via the INDEXED REST API rather than the SDK.
 *
 *  findV2Pools (SDK) is correct but wrong for a picker, and the MET test
 *  showed exactly why: it ranks by PoolState.liquidity, a Q64 value whose
 *  scale depends on the pair's price. It is not comparable across pools —
 *  MET against a $0.000175 token outranked MET-USDC at $2.1M TVL. Real depth
 *  is USD TVL, and only the indexed API has it. It also returns 364 rows for
 *  MET, which no dropdown should ever show.
 *
 *  Schema verified against a live response (2026-08-18):
 *    { total, pages, current_page, page_size, data: [{ address, name,
 *      token_x:{address,symbol,decimals,price}, token_y:{...}, tvl,
 *      current_price, pool_config:{ bin? base_fee_pct, ... } }] }
 *  Note token_x/token_y here map to tokenA/tokenB on chain.
 *
 *  Costs no RPC and no getProgramAccounts scan. The SDK still supplies exact
 *  pre-trade state via fetchPoolState at build time — REST for discovery,
 *  SDK for exactness, which is Meteora's own guidance. */
export async function findV2PoolsRest(mint, { limit = 12, solSlots = 6 } = {}) {
  const m = String(mint).trim()
  // Over-fetch (50 by TVL) so SOL-paired pools are not cut off by a top-12
  // window; the list is trimmed back to `limit` after the SOL-first sort.
  const url = `${V2_REST}?query=${encodeURIComponent(m)}&sort_by=tvl:desc&page_size=${Math.max(limit, 50)}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Pool lookup failed (HTTP ${res.status})`)
  const json = await res.json()
  const data = Array.isArray(json?.data) ? json.data : []
  // `query` matches on name/symbol/address, so it can return pools that do
  // not actually contain this mint. Keep only real matches.
  const rows = data
    .filter(p => p?.token_x?.address === m || p?.token_y?.address === m)
    .filter(p => !p.is_blacklisted)
    .map(p => {
      const isTokenA = p.token_x?.address === m
      const pair = isTokenA ? p.token_y : p.token_x
      return {
        pool: p.address,
        name: p.name || null,
        isTokenA,
        pairMint: pair?.address || null,
        pairSymbol: pair?.symbol || null,
        pairIsSol: pair?.address === WSOL,
        tvl: Number(p.tvl) || 0,
        feePct: p.pool_config?.base_fee_pct ?? null,
        volume24h: Number(p.volume?.['24h']) || 0,
        concentrated: !!p.pool_config?.concentrated_liquidity,
      }
    })
  // SOL-paired pools first, TVL descending within each group. Up to
  // `solSlots` SOL pools, the rest of `limit` filled by the deepest others
  // (either group backfills when the other is short). rows[0] — the panel's
  // default pick — is therefore the deepest SOL pool when one exists.
  const byTvl = (a, b) => b.tvl - a.tvl
  const sol = rows.filter(r => r.pairIsSol).sort(byTvl)
  const other = rows.filter(r => !r.pairIsSol).sort(byTvl)
  const nOther = Math.min(other.length, Math.max(limit - solSlots, limit - sol.length))
  const nSol = Math.min(sol.length, limit - nOther)
  return [...sol.slice(0, nSol), ...other.slice(0, nOther)]
}

// ════════════════════════════════════════════════════════════════════
// DLMM DEPOSIT + MULTI-DEX DISCOVERY (lp-dlmm-20261015)
// ════════════════════════════════════════════════════════════════════
//
// Discovery: DLMM pools come from dlmm.datapi.meteora.ag/pools (same shape
// as the DAMM v2 datapi: { data: [{ address, name, token_x, token_y, tvl,
// pool_config: { bin_step, base_fee_pct } }] }).
//
// Deposit: @meteora-ag/dlmm (already a dependency).
//   DLMM.create(connection, pool) -> dlmm
//   dlmm.getActiveBin() -> { binId, price, pricePerToken }
//   dlmm.initializePositionAndAddLiquidityByStrategy({ positionPubKey,
//     totalXAmount, totalYAmount, strategy: { minBinId, maxBinId,
//     strategyType, singleSidedX? }, user, slippage }) -> legacy Transaction
// The legacy tx's instructions go through sendV2Tx (same simulate / sign /
// confirm path and wallet modes as DAMM v2); the position keypair is an
// ephemeral extra signer, exactly like the v2 position NFT.

const DLMM_REST = 'https://dlmm.datapi.meteora.ag/pools'
export const DLMM_MAX_BINS = 69          // one position = 70 bins max (default size)
export const DLMM_DEFAULT_BINS = 34      // per side when two-sided

let _dlmmMod = null
async function dlmmDeps() {
  if (_dlmmMod) return _dlmmMod
  const [mod, base] = await Promise.all([import('@meteora-ag/dlmm'), v2deps()])
  const DLMM = mod.default?.create ? mod.default : (mod.default?.default || mod.DLMM)
  if (!DLMM?.create) {
    console.warn('[DLMM] package exports:', Object.keys(mod))
    throw new Error('DLMM export missing from @meteora-ag/dlmm')
  }
  const StrategyType = mod.StrategyType || mod.default?.StrategyType || { Spot: 0, Curve: 1, BidAsk: 2 }
  _dlmmMod = { ...base, DLMM, StrategyType }
  return _dlmmMod
}

/** DLMM pools holding `mint`, normalized to the same row shape as
 *  findV2PoolsRest plus type: 'dlmm' and binStep. */
export async function findDlmmPoolsRest(mint, { pageSize = 50 } = {}) {
  const m = String(mint).trim()
  const res = await fetch(`${DLMM_REST}?query=${encodeURIComponent(m)}&sort_by=tvl:desc&page_size=${pageSize}`)
  if (!res.ok) throw new Error(`DLMM pool lookup failed (HTTP ${res.status})`)
  const json = await res.json()
  const data = Array.isArray(json?.data) ? json.data : []
  return data
    .filter(p => p?.token_x?.address === m || p?.token_y?.address === m)
    .filter(p => !p.is_blacklisted)
    .map(p => {
      const isTokenA = p.token_x?.address === m
      const pair = isTokenA ? p.token_y : p.token_x
      return {
        type: 'dlmm',
        pool: p.address,
        name: p.name || null,
        isTokenA,                       // token is X
        pairMint: pair?.address || null,
        pairSymbol: pair?.symbol || null,
        pairIsSol: pair?.address === WSOL,
        tvl: Number(p.tvl) || 0,
        feePct: p.pool_config?.base_fee_pct ?? null,
        binStep: p.pool_config?.bin_step ?? null,
        volume24h: Number(p.volume?.['24h']) || 0,
      }
    })
}

/** Every depositable pool (DAMM v2 + DLMM) for a mint, labelled by type,
 *  TVL-descending. Pools at $0 TVL (not indexed yet / empty) are kept but
 *  flagged `unindexed` and sorted last. Either source failing alone is
 *  tolerated; both failing throws. */
export async function findLpPools(mint, { limit = 16 } = {}) {
  const [v2, dl] = await Promise.allSettled([findV2PoolsRest(mint, { limit: 50, solSlots: 50 }), findDlmmPoolsRest(mint)])
  if (v2.status === 'rejected' && dl.status === 'rejected') throw v2.reason
  const rows = [
    ...(v2.status === 'fulfilled' ? v2.value.map(r => ({ ...r, type: 'v2' })) : []),
    ...(dl.status === 'fulfilled' ? dl.value : []),
  ].map(r => ({ ...r, unindexed: !(r.tvl > 0) }))
  rows.sort((a, b) => (a.unindexed - b.unindexed) || (b.tvl - a.tvl))
  return {
    rows: rows.slice(0, limit),
    partialErr: v2.status === 'rejected' ? 'DAMM v2 lookup failed' : dl.status === 'rejected' ? 'DLMM lookup failed' : null,
  }
}

/** Live pool facts for the DLMM form: mints, decimals, active bin, price
 *  (Y per X, UI units), bin step. Read-only. */
export async function getDlmmPoolInfo(pool) {
  const { DLMM, connection, web3 } = await dlmmDeps()
  const dlmm = await DLMM.create(connection, new web3.PublicKey(String(pool).trim()))
  const active = await dlmm.getActiveBin()
  const mintX = dlmm.tokenX.publicKey.toBase58()
  const mintY = dlmm.tokenY.publicKey.toBase58()
  const [infoX, infoY] = await mintInfo([mintX, mintY])
  return {
    pool: String(pool).trim(), dlmm,
    mintX, mintY, infoX, infoY,
    activeBinId: active.binId,
    priceYperX: Number(active.pricePerToken ?? dlmm.fromPricePerLamport(Number(active.price))),
    binStep: Number(dlmm.lbPair.binStep),
    ts: Date.now(),
  }
}

/** Bin range for a deposit. One-sided X sits ABOVE the active bin (sells X
 *  as price rises), one-sided Y sits BELOW (buys X as price falls); two-sided
 *  is centred. `bins` = width per side (two-sided) or total (one-sided). */
export function dlmmRange(activeBinId, hasX, hasY, bins) {
  const w = Math.max(1, Math.min(DLMM_MAX_BINS, Math.round(Number(bins) || DLMM_DEFAULT_BINS)))
  if (hasX && !hasY) return { minBinId: activeBinId, maxBinId: activeBinId + w, singleSidedX: true }
  if (hasY && !hasX) return { minBinId: activeBinId - w, maxBinId: activeBinId }
  const half = Math.min(w, Math.floor(DLMM_MAX_BINS / 2))
  return { minBinId: activeBinId - half, maxBinId: activeBinId + half }
}

/** Open a DLMM Spot position. tokenUi / pairUi are UI strings for THIS
 *  token and the pool's other token; either may be empty (one-sided). */
export async function openDlmmPosition({
  info, tokenIsX, tokenUi, pairUi, bins = DLMM_DEFAULT_BINS,
  owner, signTransaction, slippagePct = 1, onStatus = () => {},
}) {
  if (!info?.dlmm) throw new Error('Pool not loaded')
  if (!owner) throw new Error('No wallet connected')
  const { web3, BN, StrategyType } = await dlmmDeps()
  const has = (s) => s != null && String(s).trim() !== '' && Number(s) > 0
  const xUi = tokenIsX ? tokenUi : pairUi
  const yUi = tokenIsX ? pairUi : tokenUi
  const hasX = has(xUi), hasY = has(yUi)
  if (!hasX && !hasY) throw new Error('Enter an amount')
  const totalXAmount = hasX ? toBase(BN, xUi, info.infoX.decimals) : new BN(0)
  const totalYAmount = hasY ? toBase(BN, yUi, info.infoY.decimals) : new BN(0)

  onStatus('building')
  // Re-read the active bin right before building: on a mover it shifts fast.
  const active = await info.dlmm.getActiveBin()
  const r = dlmmRange(active.binId, hasX, hasY, bins)
  const position = web3.Keypair.generate()     // EPHEMERAL, like the v2 NFT
  const ownerPk = new web3.PublicKey(String(owner))
  const legacy = await info.dlmm.initializePositionAndAddLiquidityByStrategy({
    positionPubKey: position.publicKey,
    totalXAmount, totalYAmount,
    strategy: {
      minBinId: r.minBinId, maxBinId: r.maxBinId,
      strategyType: StrategyType.Spot,
      ...(r.singleSidedX ? { singleSidedX: true } : {}),
    },
    user: ownerPk,
    slippage: slippagePct,
  })
  const res = await sendV2Tx({
    instructions: legacy.instructions, owner, signTransaction,
    extraSigners: [position], onStatus,
  })
  return { ...res, position: position.publicKey.toBase58(), range: r }
}

// ─── DLMM claim / close (lp-dlmm-close-20261015) ───
// Portfolio rows are per POOL, so actions act on every position this wallet
// holds in that pool, read fresh from chain (getPositionsByUserAndLbPair)
// rather than parsed out of the REST payload. SDK calls return arrays of
// legacy Transactions; each goes through sendV2Tx IN ORDER and the run stops
// at the first one that isn't confirmed.

async function dlmmUserPositions(pool, owner) {
  const { DLMM, connection, web3 } = await dlmmDeps()
  const dlmm = await DLMM.create(connection, new web3.PublicKey(String(pool).trim()))
  const ownerPk = new web3.PublicKey(String(owner))
  const { userPositions } = await dlmm.getPositionsByUserAndLbPair(ownerPk)
  return { dlmm, ownerPk, positions: userPositions || [] }
}

async function sendDlmmTxs(txs, { owner, signTransaction, onStatus }) {
  const sigs = []
  for (let i = 0; i < txs.length; i++) {
    const step = txs.length > 1 ? ` (${i + 1}/${txs.length})` : ''
    const r = await sendV2Tx({
      instructions: txs[i].instructions, owner, signTransaction,
      onStatus: (s, extra) => onStatus(s, { ...extra, step }),
    })
    sigs.push(r.signature)
    if (r.state !== 'confirmed') return { state: r.state, signature: r.signature, signatures: sigs, done: i, total: txs.length }
  }
  return { state: 'confirmed', signature: sigs[sigs.length - 1] || null, signatures: sigs, done: txs.length, total: txs.length }
}

/** Claim swap fees + LM rewards on every position in `pool`. */
export async function claimDlmmFees({ pool, owner, signTransaction, onStatus = () => {} }) {
  if (!owner) throw new Error('No wallet connected')
  onStatus('building')
  const { dlmm, ownerPk, positions } = await dlmmUserPositions(pool, owner)
  if (!positions.length) throw new Error('No open DLMM position in this pool')
  const txs = []
  for (const position of positions) txs.push(...await dlmm.claimAllRewardsByPosition({ owner: ownerPk, position }))
  if (!txs.length) throw new Error('Nothing to claim')
  return sendDlmmTxs(txs, { owner, signTransaction, onStatus })
}

/** Withdraw 100%, claim, and close every position in `pool` (rent back). */
export async function closeDlmmPositions({ pool, owner, signTransaction, onStatus = () => {} }) {
  if (!owner) throw new Error('No wallet connected')
  onStatus('building')
  const { dlmm, ownerPk, positions } = await dlmmUserPositions(pool, owner)
  if (!positions.length) throw new Error('No open DLMM position in this pool')
  const { BN } = await dlmmDeps()
  const txs = []
  for (const position of positions) {
    const pd = position.positionData
    const empty = Number(pd.totalXAmount || 0) === 0 && Number(pd.totalYAmount || 0) === 0
    if (empty) {
      // Nothing to withdraw: claim leftovers, then a plain close.
      txs.push(...await dlmm.claimAllRewardsByPosition({ owner: ownerPk, position }).catch(() => []))
      txs.push(await dlmm.closePosition({ owner: ownerPk, position }))
    } else {
      txs.push(...await dlmm.removeLiquidity({
        user: ownerPk, position: position.publicKey,
        fromBinId: pd.lowerBinId, toBinId: pd.upperBinId,
        bps: new BN(10_000), shouldClaimAndClose: true,
      }))
    }
  }
  return sendDlmmTxs(txs, { owner, signTransaction, onStatus })
}

// ─── Shared internals for utils/meteoraTrade.js (orders-venue-20261016) ───
// Same lazy SDK loaders + simulate/sign/confirm path the LP panel uses, so
// Meteora swaps and limit orders behave exactly like LP actions on send.
export { v2deps as _v2deps, dlmmDeps as _dlmmDeps, sendV2Tx as _sendV2Tx, mintInfo as _mintInfo }
